import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

function buildTree(nodes) {
  const map = {};
  nodes.forEach(n => { map[n.role] = { ...n, children: [] }; });
  const roots = [];
  nodes.forEach(n => {
    if (n.reports_to_role && map[n.reports_to_role]) {
      map[n.reports_to_role].children.push(map[n.role]);
    } else if (!n.reports_to_role) {
      roots.push(map[n.role]);
    }
  });
  return roots;
}

function OrgNode({ node, depth, isSuperAdmin, onEdit, onAdd, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const nodeUsers = node.occupants || [];
  const hasChildren = node.children && node.children.length > 0;
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',margin:'0 8px'}}>
      <div style={{position:'relative',display:'flex',flexDirection:'column',alignItems:'center'}}>
        {depth > 0 && <div style={{width:2,height:24,background:'#C8BFA8'}} />}
        <div style={{background:node.bg,border:'2px solid '+node.color,borderRadius:10,padding:'12px 16px',
          minWidth:160,maxWidth:200,textAlign:'center',position:'relative',cursor:hasChildren?'pointer':'default'}}
          onClick={()=>hasChildren&&setExpanded(!expanded)}>
          {node.vacant && (
            <div style={{position:'absolute',top:-8,right:-8,background:'#8F2E24',color:'#fff',
              fontSize:9,padding:'2px 6px',borderRadius:10,fontWeight:700}}>VACANT</div>
          )}
          <div style={{fontSize:12,fontWeight:700,color:node.color,marginBottom:4}}>{node.label}</div>
          <div style={{fontSize:10,color:'#8A8A8A',marginBottom:6}}>{node.dept}</div>
          {nodeUsers.length > 0 ? nodeUsers.map(u => (
            <div key={u.id} style={{fontSize:11,fontWeight:600,color:'#3B2A21',
              background:'rgba(255,255,255,0.7)',borderRadius:6,padding:'2px 8px',marginTop:2}}>{u.name}</div>
          )) : (
            <div style={{fontSize:10,color:'#8A8A8A',fontStyle:'italic'}}>No staff assigned</div>
          )}
          {hasChildren && (
            <div style={{fontSize:10,color:node.color,marginTop:4}}>{expanded?'▲':'▼'} {node.children.length}</div>
          )}
          {isSuperAdmin && (
            <div style={{display:'flex',gap:4,justifyContent:'center',marginTop:8}}>
              <button style={{fontSize:9,padding:'2px 8px',borderRadius:4,border:'1px solid '+node.color,
                background:'transparent',color:node.color,cursor:'pointer'}}
                onClick={e=>{e.stopPropagation();onEdit(node);}}>Edit</button>
              <button style={{fontSize:9,padding:'2px 8px',borderRadius:4,border:'1px solid #556B2F',
                background:'transparent',color:'#556B2F',cursor:'pointer'}}
                onClick={e=>{e.stopPropagation();onAdd(node.role);}}>+ Add</button>
              {depth > 0 && <button style={{fontSize:9,padding:'2px 8px',borderRadius:4,border:'1px solid #8F2E24',
                background:'transparent',color:'#8F2E24',cursor:'pointer'}}
                onClick={e=>{e.stopPropagation();onDelete(node);}}>✕</button>}
            </div>
          )}
        </div>
      </div>
      {hasChildren && expanded && (
        <div style={{display:'flex',flexDirection:'row',alignItems:'flex-start',
          borderTop:'2px solid #C8BFA8',marginTop:0,paddingTop:0,position:'relative'}}>
          {node.children.sort((a,b)=>a.sort_order-b.sort_order).map((child,i) => (
            <OrgNode key={child.role} node={child} depth={depth+1}
              isSuperAdmin={isSuperAdmin} onEdit={onEdit} onAdd={onAdd} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = { role:'', label:'', dept:'', reports_to_role:'', color:'#556B2F', bg:'#F2EFE7', sort_order:0, vacant:false };

export default function Organogram() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editNode, setEditNode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('tree');

  const load = () => {
    setLoading(true);
    get('/organogram')
      .then(org => {
        setNodes(org.data || []);
      })
      .catch(error => {
        window.alert(
          error.message ||
          'The organisational chart could not be loaded.'
        );
      })
      .finally(() =>
        setLoading(false)
      );
  };

  useEffect(() => { load(); }, []);

  const tree = buildTree(nodes);

  const openEdit = (node) => {
    setEditNode(node);
    setForm({ role:node.role, label:node.label, dept:node.dept||'', reports_to_role:node.reports_to_role||'',
      color:node.color||'#556B2F', bg:node.bg||'#F2EFE7', sort_order:node.sort_order||0, vacant:node.vacant||false });
    setShowForm(true);
  };

  const openAdd = (parentRole) => {
    setEditNode(null);
    setForm({ ...EMPTY_FORM, reports_to_role: parentRole });
    setShowForm(true);
  };

  const handleDelete = async (node) => {
    if (!window.confirm('Remove "'+node.label+'" from the organogram?')) return;
    await del('/organogram/'+node.id);
    load();
  };

  const handleSave = async () => {
    if (!form.role || !form.label) { alert('Role and label required'); return; }
    setSaving(true);
    try {
      if (editNode) await put('/organogram/'+editNode.id, form);
      else await post('/organogram', form);
      setShowForm(false);
      load();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Organisational Chart</h2>
        <div style={{display:'flex',gap:8}}>
          <button className={'btn btn-sm '+(view==='tree'?'btn-primary':'btn-outline')} onClick={()=>setView('tree')}>Tree View</button>
          <button className={'btn btn-sm '+(view==='list'?'btn-primary':'btn-outline')} onClick={()=>setView('list')}>List View</button>
          {isSuperAdmin && <button className="btn btn-primary btn-sm" onClick={()=>openAdd('')}>+ Add Position</button>}
        </div>
      </div>

      {view === 'tree' ? (
        <div style={{overflowX:'auto',padding:'24px 0'}}>
          <div style={{display:'flex',justifyContent:'center',minWidth:'max-content'}}>
            {tree.map(root => (
              <OrgNode key={root.role} node={root} depth={0}
                isSuperAdmin={isSuperAdmin} onEdit={openEdit} onAdd={openAdd} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead><tr style={{background:'#556B2F'}}>
              {['Position','Department','Reports To','Status'].map(h=>(
                <th key={h} style={{color:'#F2EFE7',padding:'10px 14px'}}>{h}</th>
              ))}
              {isSuperAdmin && <th style={{color:'#F2EFE7',padding:'10px 14px'}}></th>}
            </tr></thead>
            <tbody>
              {nodes.map((n,i) => {
                const parent = nodes.find(p => p.role === n.reports_to_role);
                return (
                  <tr key={n.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                    <td style={{padding:'10px 14px',fontWeight:500}}>
                      {n.label}
                      {n.vacant && <span style={{fontSize:9,background:'#8F2E24',color:'#fff',padding:'1px 6px',borderRadius:10,marginLeft:6}}>VACANT</span>}
                    </td>
                    <td style={{padding:'10px 14px',color:'#8A8A8A',fontSize:13}}>{n.dept||'—'}</td>
                    <td style={{padding:'10px 14px',fontSize:13}}>{parent?.label||'—'}</td>
                    <td style={{padding:'10px 14px'}}>
                      {(n.occupants || []).length > 0 ? (
                          (n.occupants || []).map(person=>(
                            <span
                              key={person.id}
                              style={{
                                fontSize:11,
                                background:'#E8F5EB',
                                color:'#2E6B3A',
                                padding:'2px 8px',
                                borderRadius:10,
                                marginRight:4,
                                marginBottom:3,
                                display:'inline-block'
                              }}
                            >
                              {person.name}
                            </span>
                          ))
                        ) : (
                          <span style={{
                            fontSize:11,
                            color:'#8A8A8A',
                            fontStyle:'italic'
                          }}>
                            Vacant
                          </span>
                        )}
                    </td>
                    {isSuperAdmin && (
                      <td style={{padding:'10px 14px'}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(n)}>Edit</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">{editNode?'Edit Position':'Add Position'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role (system name) *</label>
                  <input className="form-control" value={form.role}
                    onChange={e=>setForm({...form,role:e.target.value})}
                    placeholder="e.g. graphics_designer" disabled={!!editNode} />
                </div>
                <div className="form-group">
                  <label className="form-label">Display Label *</label>
                  <input className="form-control" value={form.label}
                    onChange={e=>setForm({...form,label:e.target.value})}
                    placeholder="e.g. Graphics Designer" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-control" value={form.dept}
                    onChange={e=>setForm({...form,dept:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reports To (role)</label>
                  <select className="form-control" value={form.reports_to_role}
                    onChange={e=>setForm({...form,reports_to_role:e.target.value})}>
                    <option value="">— Top level —</option>
                    {nodes.filter(n=>n.role!==form.role).map(n=>(
                      <option key={n.role} value={n.role}>{n.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-control" value={form.sort_order}
                    onChange={e=>setForm({...form,sort_order:parseInt(e.target.value)||0})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Mark as Vacant</label>
                  <select className="form-control" value={form.vacant?'true':'false'}
                    onChange={e=>setForm({...form,vacant:e.target.value==='true'})}>
                    <option value="false">No</option>
                    <option value="true">Yes — Vacant</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Text Color</label>
                  <input type="color" className="form-control" value={form.color}
                    onChange={e=>setForm({...form,color:e.target.value})} style={{height:40}} />
                </div>
                <div className="form-group">
                  <label className="form-label">Background Color</label>
                  <input type="color" className="form-control" value={form.bg}
                    onChange={e=>setForm({...form,bg:e.target.value})} style={{height:40}} />
                </div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving?'Saving...':editNode?'Save Changes':'Add Position'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
